import React, { useEffect, useMemo, useState } from 'react';
import { Gamepad2, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { useLang, tx } from '../i18n/LanguageContext';
import { useLibraryList } from '../hooks/useLibraryList';
import { PageHero } from '../components/articles/ArticlesHero';
import { LibraryPagination } from '../components/library/LibraryPagination';
import { ListHeading } from '../components/library/LibraryCta';
import { SimToolbar } from '../components/simulations/SimToolbar';
import { SimCategoryTiles } from '../components/simulations/SimCategoryTiles';
import { SimCard } from '../components/simulations/SimCard';
import { SimExtras } from '../components/simulations/SimExtras';
import { normalizeSim, SIM_HERO_IMG } from '../components/simulations/simUtils';

export default function Simulations() {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const [sims, setSims] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [level, setLevel] = useState('all');
  const byLevel = useMemo(() => (level === 'all' ? sims : sims.filter(s => String(s.level) === level)), [sims, level]);
  const list = useLibraryList(byLevel);

  useEffect(() => {
    Promise.all([api.get('/simulations'), api.get('/simulation-categories')])
      .then(([s, c]) => { setSims((s.data || []).map(normalizeSim)); setCategories(c.data || []); })
      .catch(() => setSims([]))
      .finally(() => setLoading(false));
  }, []);

  const reset = () => { list.reset(); setLevel('all'); };
  const scrollToGrid = () => document.getElementById('sims-list-top')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <main data-testid="simulations-page" className="page-y min-h-screen">
      <div className="site-container">
        <PageHero
          testId="sims-hero" image={SIM_HERO_IMG} BadgeIcon={Gamepad2}
          badge={L('المحاكاة التفاعلية', 'Interactive simulations')}
          title={L('المحاكاة التفاعلية', 'Interactive Simulations')}
          highlight={L('تجربة عملية آمنة', 'A safe, hands-on')} rest={L('وواقعية', 'and realistic experience')}
          desc={L('تعلّم مبادئ الأتمتة والتحكم الصناعي من خلال محاكاة تفاعلية تتيح لك تجربة الأجهزة والبرامج وفهم طريقة عملها خطوة بخطوة.', 'Learn industrial automation and control through interactive simulations that let you try devices and programs and understand how they work, step by step.')} />
        <SimToolbar list={list} level={level} setLevel={setLevel} categories={categories} onReset={reset} />
        <SimCategoryTiles categories={categories} sims={byLevel} active={list.cat} onChange={list.setCat} />
        <ListHeading Icon={Gamepad2} title={L('جميع المحاكاة التفاعلية', 'All interactive simulations')} list={list} unit={{ ar: 'محاكاة', en: 'simulations' }} testId="sims-count" />
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
        ) : list.filtered.length === 0 ? (
          <div data-testid="sims-empty" className="text-center text-slate-500 py-16 bg-white rounded-2xl border border-dashed border-slate-200">{L('لا توجد محاكاة مطابقة', 'No matching simulations')}</div>
        ) : (
          <>
            <div data-testid="sims-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {list.visible.map((s, i) => <SimCard key={s.id} sim={s} index={i} />)}
            </div>
            <LibraryPagination list={list} testPrefix="sims" />
          </>
        )}
        <SimExtras onStart={scrollToGrid} />
      </div>
    </main>
  );
}
